import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  CardActions,
  Divider,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { products as productsAPI, brands as brandsAPI, categories as categoriesAPI, suppliers as suppliersAPI, clearDropdownCache } from '../services/api';
import { getBrandColor, getBrandLogo, getBrandTextColor } from '../utils/brandUtils';
import { useAuth } from '../services/auth';

function Products() {
  const { canCreate, canEdit, canDelete, isViewer } = useAuth();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    model: '',
    brand: '',
    part_category: '',
    RetailPrice: '',
    WholesalePrice: '',
    description: '',
    supplier: '',
    initial_stock: {
      MJTech: '',
      'Mobile Care': ''
    }
  });

  const [showNewFields, setShowNewFields] = useState({
    brand: false,
    category: false,
  });

  const [newOptions, setNewOptions] = useState({
    brand: '',
    category: '',
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCategory, selectedBrand, selectedSupplier, page, rowsPerPage, searchTerm]);

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setSelectedSupplier('');
    setSearchTerm('');
    setPage(0);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query parameters for filtered products
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (selectedCategory) params.part_category = selectedCategory;
      if (selectedBrand) params.brand = selectedBrand;
      if (selectedSupplier) params.supplier = selectedSupplier;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const [productsRes, brandsRes, categoriesRes, suppliersRes] = await Promise.all([
        productsAPI.getFiltered(params),
        brandsAPI.getLite(), // Use cached lightweight endpoint
        categoriesAPI.getLite(), // Use cached lightweight endpoint
        suppliersAPI.getLite(), // Use cached lightweight endpoint
      ]);

      const products = productsRes.data.Products || [];
      setProducts(products);
      setTotal(productsRes.data.Total || 0);

      // Get filter options from API endpoints, but also include options from current products
      const apiBrands = brandsRes.data.Brands || [];
      const apiCategories = categoriesRes.data.Categories || [];
      const apiSuppliers = suppliersRes.data.Suppliers || [];

      // Extract unique brands, categories, and suppliers from current products
      const productBrands = [...new Set(products.map(p => p.Brand).filter(Boolean))];
      const productCategories = [...new Set(products.map(p => p.PartCategory).filter(Boolean))];
      const productSuppliers = [...new Set(products.map(p => p.Supplier).filter(Boolean))];

      // Combine API data with product data to ensure all options are available
      const allBrands = [...new Set([...apiBrands, ...productBrands])].sort();
      const allCategories = [...new Set([...apiCategories, ...productCategories])].sort();
      const allSuppliers = [...new Set([...apiSuppliers, ...productSuppliers])].sort();

      setBrands(allBrands);
      setCategories(allCategories);
      setSuppliers(allSuppliers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewOptionChange = (field, value) => {
    setNewOptions(prev => ({ ...prev, [field]: value }));
  };

  const handleAddNewOption = (field) => {
    if (newOptions[field].trim()) {
      if (field === 'brand') {
        setNewProduct(prev => ({
          ...prev,
          brand: newOptions[field].trim()
        }));
      } else if (field === 'category') {
        setNewProduct(prev => ({
          ...prev,
          part_category: newOptions[field].trim()
        }));
      }
      setShowNewFields(prev => ({ ...prev, [field]: false }));
      setNewOptions(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddProduct = async () => {
    try {
      const productData = {
        ...newProduct,
        Brand: newProduct.brand ? newProduct.brand.toUpperCase().trim() : '',
        PartCategory: newProduct.part_category ? newProduct.part_category.toUpperCase().trim() : '',
        RetailPrice: parseFloat(newProduct.RetailPrice),
        WholesalePrice: newProduct.WholesalePrice ? parseFloat(newProduct.WholesalePrice) : null,
        // Convert initial_stock object to the format expected by backend
        InitialStock: {}
      };

      // Add initial stock data
      Object.entries(newProduct.initial_stock).forEach(([branchName, quantity]) => {
        if (quantity && !isNaN(parseInt(quantity))) {
          productData.InitialStock[branchName] = parseInt(quantity);
        }
      });

      await productsAPI.create(productData);

      // Clear dropdown cache since we might have created new brands/categories
      clearDropdownCache();

      setAddDialogOpen(false);
      resetNewProductForm();
      fetchData();
    } catch (err) {
      console.error('Error adding product:', err);
      setError('Failed to create product');
    }
  };

  const resetNewProductForm = () => {
    setNewProduct({
      model: '',
      brand: '',
      part_category: '',
      RetailPrice: '',
      WholesalePrice: '',
      description: '',
      supplier: '',
      initial_stock: {
        MJTech: '',
        'Mobile Care': ''
      }
    });
    setShowNewFields({
      brand: false,
      category: false,
    });
    setNewOptions({
      brand: '',
      category: '',
    });
  };

  const handleEditProduct = async () => {
    if (!selectedProduct) return;

    try {
      // Prepare unified update data
      const updateData = {
        Model: selectedProduct.model,
        Brand: selectedProduct.brand ? selectedProduct.brand.toUpperCase().trim() : '',
        PartCategory: selectedProduct.part_category ? selectedProduct.part_category.toUpperCase().trim() : '',
        Description: selectedProduct.description,
        Supplier: selectedProduct.supplier,
      };

      // Add pricing if available
      if (selectedProduct.Pricing && selectedProduct.Pricing[0]) {
        updateData.WholesalePrice = selectedProduct.Pricing[0].WholesalePrice || 0;
        updateData.RetailPrice = selectedProduct.Pricing[0].RetailPrice || 0;
      }

      // Add stock updates if available
      if (selectedProduct.Stock && selectedProduct.Stock.length > 0) {
        updateData.Stock = selectedProduct.Stock
          .filter(stockItem => stockItem.BranchName && stockItem.Quantity !== undefined)
          .map(stockItem => ({
            BranchName: stockItem.BranchName,
            Quantity: stockItem.Quantity || 0
          }));
      }

      // Send single unified update request
      await productsAPI.update(selectedProduct.Id, updateData);

      setEditDialogOpen(false);
      setSelectedProduct(null);
      fetchData();
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product');
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      await productsAPI.delete(selectedProduct.Id);
      // Clear dropdown cache to refresh categories/brands/suppliers after deletion
      clearDropdownCache();
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product');
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(price);
  };

  // Remove local functions since we're now using shared utilities from brandUtils

  const renderSelectField = (field, label, options, required = false) => {
    const showNew = showNewFields[field];
    const fieldOptions = options || [];
    const currentValue = field === 'brand' ? newProduct.brand : newProduct.part_category;

    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={currentValue || ''}
          onChange={(e) => {
            if (e.target.value === 'ADD_NEW') {
              setShowNewFields(prev => ({ ...prev, [field]: true }));
            } else {
              if (field === 'brand') {
                setNewProduct(prev => ({ ...prev, brand: e.target.value }));
              } else if (field === 'category') {
                setNewProduct(prev => ({ ...prev, part_category: e.target.value }));
              }
              // Reset showNew when selecting an existing option
              setShowNewFields(prev => ({ ...prev, [field]: false }));
            }
          }}
          label={label}
          renderValue={(selected) => {
            if (!selected) {
              return <em>Select {label}</em>;
            }
            return selected;
          }}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {fieldOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          {/* Only show Add New option if there's no current value or if in showNew mode */}
          {(!currentValue || showNew) && (
            <MenuItem value="ADD_NEW">
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                Add New {label}
              </Box>
            </MenuItem>
          )}
        </Select>
        {showNew && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={`Enter new ${label.toLowerCase()}`}
              value={newOptions[field]}
              onChange={(e) => handleNewOptionChange(field, e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddNewOption(field);
                }
              }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => handleAddNewOption(field)}
              disabled={!newOptions[field].trim()}
            >
              Add
            </Button>
            <Button
              size="small"
              onClick={() => setShowNewFields(prev => ({ ...prev, [field]: false }))}
            >
              Cancel
            </Button>
          </Box>
        )}
      </FormControl>
    );
  };

  // Mobile Product Card Component
  const ProductCard = ({ product }) => (
    <Card
      sx={{
        mb: 2,
        background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
        border: '1px solid rgba(0, 191, 255, 0.2)',
        '&:hover': {
          border: '1px solid rgba(0, 191, 255, 0.4)',
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              mr: 2,
              backgroundColor: getBrandColor(product.Brand),
              color: getBrandTextColor(product.Brand),
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {getBrandLogo(product.Brand)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {product.Brand} {product.Model}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {product.Brand && (
                <Chip
                  label={product.Brand}
                  size="small"
                  sx={{
                    backgroundColor: `${getBrandColor(product.Brand)}40`,
                    color: getBrandTextColor(product.Brand),
                    fontWeight: 600,
                    border: `1px solid ${getBrandColor(product.Brand)}60`,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              <Chip
                label={product.PartCategory || 'Other'}
                size="small"
                color="secondary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>
          {!isViewer() && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {canEdit() && (
                <IconButton
                  onClick={() => {
                    const formProduct = {
                      ...product,
                      model: product.Model,
                      brand: product.Brand,
                      part_category: product.PartCategory,
                      description: product.Description,
                      supplier: product.Supplier,
                      pricing: product.Pricing
                    };
                    setSelectedProduct(formProduct);
                    setEditDialogOpen(true);
                  }}
                  size="small"
                  sx={{ color: 'primary.main', p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete() && (
                <IconButton
                  onClick={() => {
                    const formProduct = {
                      ...product,
                      model: product.Model,
                      brand: product.Brand,
                      part_category: product.PartCategory,
                      description: product.Description,
                      supplier: product.Supplier,
                      pricing: product.Pricing
                    };
                    setSelectedProduct(formProduct);
                    setDeleteDialogOpen(true);
                  }}
                  size="small"
                  sx={{ color: 'error.main', p: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              Capital
            </Typography>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {product.Pricing && product.Pricing.length > 0 && product.Pricing[0].WholesalePrice
                ? formatPrice(product.Pricing[0].WholesalePrice)
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              Retail
            </Typography>
            <Typography variant="h6" fontWeight={600} color="primary">
              {product.Pricing && product.Pricing.length > 0 && product.Pricing[0].RetailPrice
                ? formatPrice(product.Pricing[0].RetailPrice)
                : '-'}
            </Typography>
          </Grid>
        </Grid>

        {product.Pricing && product.Pricing.length > 0 && product.Pricing[0].WholesalePrice && product.Pricing[0].RetailPrice && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Margin: {formatPrice(product.Pricing[0].RetailPrice - product.Pricing[0].WholesalePrice)} ({(((product.Pricing[0].RetailPrice - product.Pricing[0].WholesalePrice) / product.Pricing[0].WholesalePrice) * 100).toFixed(1)}%)
            </Typography>
            <Chip
              label={((product.Pricing[0].RetailPrice - product.Pricing[0].WholesalePrice) / product.Pricing[0].WholesalePrice) * 100 > 20 ? 'Healthy' : 'Low'}
              size="small"
              color={((product.Pricing[0].RetailPrice - product.Pricing[0].WholesalePrice) / product.Pricing[0].WholesalePrice) * 100 > 20 ? 'success' : 'warning'}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Product Management
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Manage your product catalog and inventory items
          </Typography>
        </Box>
        {canCreate() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              color: '#000000',
              px: 3,
              py: 1.5,
              alignSelf: { xs: 'stretch', sm: 'flex-start' },
            }}
          >
            Add Product
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isMobile ? 10 : 3}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: isMobile && (
                    <IconButton
                      onClick={() => setShowFilters(!showFilters)}
                      size="small"
                      sx={{ color: showFilters ? 'primary.main' : 'text.secondary' }}
                    >
                      <FilterIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {isMobile && (
              <Grid item xs={2}>
                <Tooltip title="Clear all filters">
                  <IconButton onClick={clearFilters} size="large">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {!isMobile && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={selectedBrand}
                      label="Brand"
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Category"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Supplier</InputLabel>
                      <Select
                        value={selectedSupplier}
                        label="Supplier"
                        onChange={(e) => setSelectedSupplier(e.target.value)}
                      >
                        <MenuItem value="">All Suppliers</MenuItem>
                        {suppliers.map((supplier) => (
                          <MenuItem key={supplier} value={supplier}>
                            {supplier}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Clear all filters">
                      <IconButton onClick={clearFilters} size="large">
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </>
            )}
            <Collapse in={isMobile && showFilters} sx={{ width: '100%' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={selectedBrand}
                      label="Brand"
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Category"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={selectedSupplier}
                      label="Supplier"
                      onChange={(e) => setSelectedSupplier(e.target.value)}
                    >
                      <MenuItem value="">All Suppliers</MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier} value={supplier}>
                          {supplier}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Display - Responsive */}
      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading products...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Mobile Card View */}
          {isMobile ? (
            <>
              <Box>
                {products.map((product) => (
                  <ProductCard key={product.Id} product={product} />
                ))}
              </Box>
              {/* Mobile Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 },
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            /* Desktop Table View */
            <Card>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Brand</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Capital Price</TableCell>
                        <TableCell align="right">Retail Price</TableCell>
                        {!isViewer() && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product) => (
                          <TableRow key={product.Id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    mr: 2,
                                    backgroundColor: getBrandColor(product.Brand),
                                    color: getBrandTextColor(product.Brand),
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {getBrandLogo(product.Brand)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {product.Brand} {product.Model}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Model: {product.Model}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {product.Brand ? (
                                <Chip
                                  label={product.Brand}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${getBrandColor(product.Brand)}40`,
                                    color: getBrandTextColor(product.Brand),
                                    fontWeight: 600,
                                    border: `1px solid ${getBrandColor(product.Brand)}60`,
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  No Brand
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {product.PartCategory}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {product.Supplier || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {product.Pricing && product.Pricing.length > 0 && product.Pricing[0].WholesalePrice
                                  ? formatPrice(product.Pricing[0].WholesalePrice)
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} color="primary">
                                {product.Pricing && product.Pricing.length > 0 && product.Pricing[0].RetailPrice
                                  ? formatPrice(product.Pricing[0].RetailPrice)
                                  : '-'}
                              </Typography>
                            </TableCell>
                            {!isViewer() && (
                              <TableCell align="center">
                                {canEdit() && (
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      // Transform PascalCase to snake_case for form fields
                                      const formProduct = {
                                        ...product,
                                        model: product.Model,
                                        brand: product.Brand,
                                        part_category: product.PartCategory,
                                        description: product.Description,
                                        supplier: product.Supplier,
                                        pricing: product.Pricing
                                      };
                                      setSelectedProduct(formProduct);
                                      setEditDialogOpen(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                )}
                                {canDelete() && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      // Transform PascalCase to snake_case for form fields
                                      const formProduct = {
                                        ...product,
                                        model: product.Model,
                                        brand: product.Brand,
                                        part_category: product.PartCategory,
                                        description: product.Description,
                                        supplier: product.Supplier,
                                        pricing: product.Pricing
                                      };
                                      setSelectedProduct(formProduct);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  sx={{ borderTop: 1, borderColor: 'divider' }}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Add Product Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
            border: '1px solid rgba(0, 191, 255, 0.2)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Model"
                value={newProduct.model}
                onChange={(e) => setNewProduct({ ...newProduct, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderSelectField('brand', 'Brand', brands)}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderSelectField('category', 'Category', categories, true)}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Price (PHP)"
                type="number"
                value={newProduct.RetailPrice}
                onChange={(e) => setNewProduct({ ...newProduct, RetailPrice: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capital Price (PHP)"
                type="number"
                value={newProduct.WholesalePrice}
                onChange={(e) => setNewProduct({ ...newProduct, WholesalePrice: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={newProduct.supplier}
                  label="Supplier"
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewProduct({ ...newProduct, supplier: value });
                  }}
                >
                  <MenuItem value="">
                    <em>Select Supplier</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier} value={supplier}>
                      {supplier}
                    </MenuItem>
                  ))}
                  <MenuItem value="ADD_NEW" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    + Add New Supplier
                  </MenuItem>
                </Select>
                {/* Show text field when "Add New" is selected or when value is not in suppliers list */}
                {(newProduct.supplier === 'ADD_NEW' || (newProduct.supplier && !suppliers.includes(newProduct.supplier))) && (
                  <TextField
                    fullWidth
                    label="Enter New Supplier"
                    value={newProduct.supplier === 'ADD_NEW' ? '' : newProduct.supplier}
                    onChange={(e) => setNewProduct({ ...newProduct, supplier: e.target.value })}
                    placeholder="Enter supplier name"
                    sx={{ mt: 2 }}
                    autoFocus
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              />
            </Grid>

            {/* Initial Stock Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Initial Stock (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MJTech Stock"
                type="number"
                value={newProduct.initial_stock.MJTech}
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  initial_stock: {
                    ...newProduct.initial_stock,
                    MJTech: e.target.value
                  }
                })}
                placeholder="0"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Care Stock"
                type="number"
                value={newProduct.initial_stock['Mobile Care']}
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  initial_stock: {
                    ...newProduct.initial_stock,
                    'Mobile Care': e.target.value
                  }
                })}
                placeholder="0"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              resetNewProductForm();
            }}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            sx={{
              color: '#000000'
            }}
          >
            Add Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedProduct(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
            border: '1px solid rgba(0, 191, 255, 0.2)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Model"
                value={selectedProduct?.model || ''}
                onChange={(e) => setSelectedProduct(prev => ({ ...prev, model: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedProduct?.brand || ''}
                  label="Brand"
                  onChange={(e) => setSelectedProduct(prev => ({ ...prev, brand: e.target.value }))}
                >
                  <MenuItem value="">Select Brand</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedProduct?.part_category || ''}
                  label="Category"
                  onChange={(e) => setSelectedProduct(prev => ({ ...prev, part_category: e.target.value }))}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={selectedProduct?.supplier || ''}
                  label="Supplier"
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedProduct(prev => ({ ...prev, supplier: value }));
                  }}
                >
                  <MenuItem value="">
                    <em>Select Supplier</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier} value={supplier}>
                      {supplier}
                    </MenuItem>
                  ))}
                  <MenuItem value="ADD_NEW" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    + Add New Supplier
                  </MenuItem>
                </Select>
                {/* Show text field when "Add New" is selected or when value is not in suppliers list */}
                {(selectedProduct?.supplier === 'ADD_NEW' || (selectedProduct?.supplier && !suppliers.includes(selectedProduct.supplier))) && (
                  <TextField
                    fullWidth
                    label="Enter New Supplier"
                    value={selectedProduct?.supplier === 'ADD_NEW' ? '' : selectedProduct?.supplier || ''}
                    onChange={(e) => setSelectedProduct(prev => ({ ...prev, supplier: e.target.value }))}
                    placeholder="Enter supplier name"
                    sx={{ mt: 2 }}
                    autoFocus
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={selectedProduct?.description || ''}
                onChange={(e) => setSelectedProduct(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>

            {/* Pricing Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Pricing
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capital Price (PHP)"
                type="number"
                value={selectedProduct?.Pricing?.[0]?.WholesalePrice || ''}
                onChange={(e) => setSelectedProduct(prev => ({
                  ...prev,
                  Pricing: [{
                    ...prev?.Pricing?.[0],
                    WholesalePrice: parseFloat(e.target.value) || 0
                  }]
                }))}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Price (PHP)"
                type="number"
                value={selectedProduct?.Pricing?.[0]?.RetailPrice || ''}
                onChange={(e) => setSelectedProduct(prev => ({
                  ...prev,
                  Pricing: [{
                    ...prev?.Pricing?.[0],
                    RetailPrice: parseFloat(e.target.value) || 0
                  }]
                }))}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>

            {/* Stock Management Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Stock Levels
              </Typography>
            </Grid>

            {/* MJTech Stock */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MJTech Stock"
                type="number"
                value={selectedProduct?.Stock?.find(s => s.BranchName === 'MJTech')?.Quantity || ''}
                onChange={(e) => {
                  const newQuantity = parseInt(e.target.value) || 0;
                  setSelectedProduct(prev => {
                    const currentStock = prev?.Stock || [];
                    const existingMJTech = currentStock.find(s => s.BranchName === 'MJTech');

                    if (existingMJTech) {
                      // Update existing entry
                      return {
                        ...prev,
                        Stock: currentStock.map(s =>
                          s.BranchName === 'MJTech'
                            ? { ...s, Quantity: newQuantity }
                            : s
                        )
                      };
                    } else {
                      // Create new entry
                      return {
                        ...prev,
                        Stock: [...currentStock, {
                          BranchName: 'MJTech',
                          Quantity: newQuantity,
                          MinStockLevel: 0,
                          MaxStockLevel: 0
                        }]
                      };
                    }
                  });
                }}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>

            {/* Mobile Care Stock */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Care Stock"
                type="number"
                value={selectedProduct?.Stock?.find(s => s.BranchName === 'Mobile Care')?.Quantity || ''}
                onChange={(e) => {
                  const newQuantity = parseInt(e.target.value) || 0;
                  setSelectedProduct(prev => {
                    const currentStock = prev?.Stock || [];
                    const existingMobileCare = currentStock.find(s => s.BranchName === 'Mobile Care');

                    if (existingMobileCare) {
                      // Update existing entry
                      return {
                        ...prev,
                        Stock: currentStock.map(s =>
                          s.BranchName === 'Mobile Care'
                            ? { ...s, Quantity: newQuantity }
                            : s
                        )
                      };
                    } else {
                      // Create new entry
                      return {
                        ...prev,
                        Stock: [...currentStock, {
                          BranchName: 'Mobile Care',
                          Quantity: newQuantity,
                          MinStockLevel: 0,
                          MaxStockLevel: 0
                        }]
                      };
                    }
                  });
                }}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedProduct(null);
            }}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditProduct}>
            Update Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedProduct(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
            border: '1px solid rgba(255, 67, 54, 0.3)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedProduct?.brand_name} {selectedProduct?.model}"?
            This action cannot be undone and will also delete all associated stock and pricing data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedProduct(null);
            }}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteProduct}
            sx={{
              color: '#000000'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Products;